import { useId, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { chromeSurface, color, layout, space } from "@tokens/index";
import { ContentSwap, Dialog, Text, useViewport } from "@primitives/index";
import { WorkflowTimelineFilters } from "../WorkflowTimelineFilters";
import { groupByDay } from "./grouping";
import { FiltersButton, TimelineHeader, type FiltersControl } from "./TimelineHeader";
import { sceneMeasures } from "../workflow-board/measures";
import { TimelineRow } from "./TimelineRow";
import {
  DisclosureBanner,
  EmptyState,
  ErrorState,
  FilteredEmptyState,
  TimelineSkeleton,
} from "./TimelineStates";
import { staleDataStyle } from "../staleData";
import { activeFilterCount, type WorkflowTimelineProps } from "./types";

// The composition fills its host. The feed column owns the scroll so the
// header and the filter rail stay put while the audit list scrolls.
const containerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  width: "100%",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
};

const bodyRowStyle: CSSProperties = {
  display: "flex",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
};

function railStyle(embedded: boolean): CSSProperties {
  return {
    width: embedded ? sceneMeasures.filterRailWidth : layout.filterRailWidth,
    flexShrink: 0,
    minHeight: 0,
    overflowY: "auto",
    paddingBlock: space[4],
    paddingInline: space[3],
    // Embedded: the rail separates from the feed by tone, not by a line.
    ...(embedded
      ? { background: color.bg.surface }
      : { borderRight: `1px solid ${color.border.subtle}` }),
  };
}

// Embedded toolbar: the filter toggle needs a home when the scene header
// owns the title row. One slim row, right-aligned, only below `expanded`.
const toolbarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  minHeight: 44,
  paddingInline: space[4],
  flexShrink: 0,
};

function feedScrollStyle(compact: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    paddingBlock: space[4],
    paddingInline: compact ? space[3] : space[4],
  };
}

// Content is capped and centered on wide monitors; extra width is margin.
function feedColumnStyle(embedded: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: space[4],
    width: "100%",
    maxWidth: embedded ? sceneMeasures.contentMaxWidth : layout.contentMaxWidth,
    marginInline: "auto",
    minWidth: 0,
  };
}

// The day groups stack with the column's gap inside the swap layer.
const feedStackStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[4],
};

const dayHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[3],
  paddingBlock: space[2],
  position: "sticky",
  top: 0,
  zIndex: 1,
  ...chromeSurface,
};

const dayDividerStyle: CSSProperties = {
  flex: 1,
  height: "1px",
  background: color.border.subtle,
};

// Off-screen days skip paint and layout. The windowing sits on the list,
// not on the section, so the sticky day header is not clipped.
const eventListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
  listStyle: "none",
  padding: 0,
  margin: 0,
  contentVisibility: "auto",
  containIntrinsicSize: "auto 600px",
};

export function WorkflowTimeline({
  events,
  isLoading,
  isStale = false,
  error,
  truncated,
  partial,
  onBack,
  onRetry,
  workflowTitle,
  subject,
  viewSwitcher,
  embedded = false,
  filters,
}: WorkflowTimelineProps): ReactElement {
  const { isCompact, isAtLeast } = useViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const railId = useId();
  const groups = useMemo(() => groupByDay(events), [events]);
  const subjectKind = subject?.kind ?? "workflow";
  const subjectTitle = subject?.title ?? workflowTitle;

  const filterActive = activeFilterCount(filters) > 0;
  const showFilters =
    filters !== undefined &&
    !isLoading &&
    error == null &&
    (filters.workItems.length > 0 || filters.activityTypes.length > 0);

  const railMode: "fixed" | "collapsible" | "sheet" = isAtLeast("expanded")
    ? "fixed"
    : isCompact
      ? "sheet"
      : "collapsible";
  const railVisible =
    showFilters && (railMode === "fixed" || (railMode === "collapsible" && filtersOpen));
  // The fixed rail's column is reserved while the first feed loads, so the
  // skeleton already sits where the events will land and nothing shifts.
  const railReserved = !railVisible && isLoading && filters === undefined && railMode === "fixed";
  const sheetOpen = showFilters && railMode === "sheet" && filtersOpen;

  const filtersControl: FiltersControl | undefined =
    showFilters && railMode !== "fixed"
      ? {
          mode: railMode,
          open: filtersOpen,
          count: activeFilterCount(filters),
          onToggle: () => setFiltersOpen((open) => !open),
          controlsId: railId,
        }
      : undefined;

  const filterContent =
    filters !== undefined ? (
      <WorkflowTimelineFilters
        workItems={filters.workItems}
        selectedWorkItemId={filters.selectedWorkItemId}
        onSelectWorkItem={filters.onSelectWorkItem}
        activityTypes={filters.activityTypes}
        hiddenActivityTypes={filters.hiddenActivityTypes}
        onToggleActivityType={filters.onToggleActivityType}
        onResetActivityTypes={filters.onResetActivityTypes}
      />
    ) : null;

  return (
    <div
      style={containerStyle}
      role="region"
      aria-label={`${subjectKind === "queue" ? "Queue" : "Workflow"} audit timeline`}
    >
      {embedded ? (
        filtersControl !== undefined ? (
          <div style={toolbarStyle}>
            <FiltersButton control={filtersControl} />
          </div>
        ) : null
      ) : (
        <TimelineHeader
          onBack={onBack}
          subjectTitle={subjectTitle}
          viewSwitcher={viewSwitcher}
          filtersControl={filtersControl}
          stacked={isCompact}
        />
      )}

      <div style={bodyRowStyle}>
        {railVisible ? (
          <aside id={railId} aria-label="Timeline filters" style={railStyle(embedded)}>
            {filterContent}
          </aside>
        ) : railReserved ? (
          <div aria-hidden="true" style={railStyle(embedded)} />
        ) : null}

        <div style={feedScrollStyle(isCompact)}>
          <div style={{ ...feedColumnStyle(embedded), ...staleDataStyle(isStale) }}>
            {(truncated || partial) && !isLoading && error == null ? (
              <DisclosureBanner truncated={truncated} partial={partial} subjectKind={subjectKind} />
            ) : null}

            <ContentSwap
              ready={!isLoading}
              skeleton={<TimelineSkeleton />}
              layerStyle={feedStackStyle}
            >
              {error != null ? (
                <ErrorState error={error} onRetry={onRetry} />
              ) : events.length === 0 ? (
                filterActive ? (
                  <FilteredEmptyState onClear={() => filters?.onClearAll()} />
                ) : (
                  <EmptyState subjectKind={subjectKind} />
                )
              ) : (
                groups.map((group) => (
                  <section key={group.key} aria-label={group.label}>
                    <div style={dayHeaderStyle}>
                      <Text role="caps" size="xs" tone="muted">
                        {group.label}
                      </Text>
                      <span style={dayDividerStyle} />
                    </div>
                    <ul style={eventListStyle}>
                      {group.events.map((event) => (
                        <li key={event.activityId}>
                          <TimelineRow event={event} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))
              )}
            </ContentSwap>
          </div>
        </div>
      </div>

      {filters !== undefined && railMode === "sheet" ? (
        <Dialog
          open={sheetOpen}
          onClose={() => setFiltersOpen(false)}
          title="Filters"
          size="sm"
          presentation="auto"
        >
          {filterContent}
        </Dialog>
      ) : null}
    </div>
  );
}
